/**
 * Local file import.
 *
 * Parses a file the user picked off their machine into a plain GeoJSON
 * FeatureCollection — the same shape the ArcGIS FeatureServer path already
 * produces — so a local layer renders, identifies, and styles through the
 * existing pipeline rather than a parallel one.
 *
 * FORMATS: GeoJSON/JSON, KML, KMZ, zipped Shapefile, and delimited text
 * (CSV/TSV) with coordinate columns — all parsed with platform built-ins, no
 * dependency.
 *
 * THE ONE EXCEPTION: FILE GEODATABASE
 * A zipped .gdb (and a .gpkg, for the same reason) is handed to the API
 * instead, because the only reader for Esri's binary format is GDAL and the
 * browser has none. See `gdb.ts`. It still arrives back here as the same
 * `ParsedLocalLayer` a KML produces, so nothing downstream knows the
 * difference — the only visible change is that its features are fetched when
 * the user picks the layer rather than parsed up front.
 */

import type { LayerGeometryType, LayerRenderer, LocalRasterData } from '../types';
import {
  fetchGeodatabaseLayer,
  looksLikeGeodatabase,
  releaseGeodatabase,
  uploadGeodatabase,
} from './gdb';
import { MAX_RASTER_FILE_BYTES, parseGeoTiffFile, parsePlainImageFile } from './geotiff';
import { extractKmlFromKmz, parseKmlText } from './kml';
import { buildShapefileFeatures } from './shapefile';
import { peekZipEntryNames, readZip } from './zip';

export { MAX_RASTER_FILE_BYTES };

export interface ParsedLocalLayer {
  name: string;
  data: GeoJSON.FeatureCollection;
  featureCount: number;
  geometryType: LayerGeometryType;
  /** Attribute names present on the features. */
  fields: string[];
  bounds?: [number, number, number, number];
  /** Styling carried over from the source (KML <Style> today). */
  renderer?: LayerRenderer;
  /** How many distinct source styles produced this layer. */
  styleCount?: number;
  /** Non-fatal notes worth showing (skipped rows, mixed geometry, …). */
  notices: string[];
  /**
   * Set when the features have not been read yet, and `data` is still empty.
   *
   * Only a geodatabase produces these: its feature classes are described on
   * upload and converted one at a time, so a file with thirty of them costs
   * one upload rather than thirty conversions nobody asked for. `resolveLocalLayers`
   * is what turns a pending layer into a loaded one.
   */
  pending?: () => Promise<ParsedLocalLayer>;
  /** Why this layer cannot be added, when it cannot. Disables it in the picker. */
  unavailable?: string;
  /**
   * Set for a GeoTIFF. `data` is a dummy empty collection when this is
   * present — there are no features, so nothing downstream that inspects
   * `data` should ever be reached without checking this first. The picker
   * and `addLocalLayer` in App.tsx are the two places that do.
   */
  raster?: LocalRasterData;
}

/** One file can produce several layers (a KML with folders, for example). */
export interface ParsedImport {
  layers: ParsedLocalLayer[];
  /** Notes about the file as a whole, rather than a single layer. */
  notices: string[];
  /** The file this came from, for anything that wants to say where. */
  sourceName?: string;
  /**
   * Frees whatever the import is holding open — today, the extracted copy of a
   * geodatabase on the server. Safe to call more than once, and safe to skip:
   * the server sweeps abandoned uploads on its own TTL.
   */
  release?: () => void;
}

export class LocalFileError extends Error {}

/** Largest file accepted. Beyond this the browser tab, not the format, is the limit. */
export const MAX_LOCAL_FILE_BYTES = 12 * 1024 * 1024;

/**
 * Largest ZIP accepted when it turns out to hold a geodatabase.
 *
 * Far higher than `MAX_LOCAL_FILE_BYTES` because the constraint is a different
 * one: a browser-parsed file has to fit in the tab alongside everything else,
 * whereas this one is streamed to the server and never held here at all. The
 * ceiling that matters for a .gdb is the server's `GDB_MAX_UPLOAD_BYTES`, and
 * this figure mirrors it so the refusal happens before the upload rather than
 * after 200 MB has crossed the wire.
 *
 * The server is the authority: if it is configured higher, it accepts more and
 * this constant should be raised to match; if lower, its 413 carries the real
 * number and is shown as-is.
 */
export const MAX_GDB_FILE_BYTES = 200 * 1024 * 1024;

const COORD_ALIASES = {
  lon: ['lon', 'lng', 'long', 'longitude', 'x', 'xcoord', 'x_coord', 'easting'],
  lat: ['lat', 'latitude', 'y', 'ycoord', 'y_coord', 'northing'],
};

function normaliseGeometry(type: string | undefined): LayerGeometryType {
  switch ((type ?? '').toLowerCase()) {
    case 'point':
    case 'multipoint':
      return 'point';
    case 'linestring':
    case 'multilinestring':
      return 'line';
    case 'polygon':
    case 'multipolygon':
      return 'polygon';
    default:
      return 'unknown';
  }
}

/** Extent of a FeatureCollection as [west, south, east, north]. */
export function computeBounds(
  data: GeoJSON.FeatureCollection,
): [number, number, number, number] | undefined {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  const visit = (coords: any): void => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const [x, y] = coords as [number, number];
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      west = Math.min(west, x);
      east = Math.max(east, x);
      south = Math.min(south, y);
      north = Math.max(north, y);
      return;
    }
    for (const part of coords) visit(part);
  };

  for (const feature of data.features) visit((feature.geometry as any)?.coordinates);

  return Number.isFinite(west) && Number.isFinite(south) ? [west, south, east, north] : undefined;
}

function summarise(
  name: string,
  features: GeoJSON.Feature[],
  notices: string[],
  extras: { renderer?: LayerRenderer; styleCount?: number; geometryType?: LayerGeometryType } = {},
): ParsedLocalLayer {
  if (features.length === 0) {
    throw new LocalFileError('No usable features were found in this file.');
  }

  const kinds = new Set(
    features.map((feature) => normaliseGeometry(feature.geometry?.type)).filter((k) => k !== 'unknown'),
  );
  if (kinds.size > 1) {
    notices.push(`Mixed geometry (${[...kinds].join(', ')}); styling uses the first type.`);
  }

  const fields = Array.from(
    new Set(features.flatMap((feature) => Object.keys(feature.properties ?? {}))),
  );

  const data: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features };

  return {
    name,
    data,
    featureCount: features.length,
    geometryType:
      extras.geometryType ?? ((kinds.values().next().value as LayerGeometryType) ?? 'unknown'),
    fields,
    bounds: computeBounds(data),
    renderer: extras.renderer,
    styleCount: extras.styleCount,
    notices,
  };
}

import { looksProjected, needsDatumShift, reprojectGeometry, resolveCrs } from './reproject';

/* ─────────────────────────────── GeoJSON ────────────────────────────────── */

function parseGeoJson(name: string, text: string): ParsedLocalLayer {
  let parsed: any;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new LocalFileError('This file is not valid JSON.');
  }

  const notices: string[] = [];
  let raw: any[];

  /* GeoJSON as published today is always WGS84 lon/lat (RFC 7946 dropped the
     `crs` member for exactly that reason), but the files people actually have
     were exported by desktop GIS years ago and often carry one — or carry
     nothing and are in metres anyway. Both were previously drawn as-is, which
     put the layer somewhere in the Atlantic. */
  const declaredCrs: string | undefined =
    parsed?.crs?.properties?.name ?? parsed?.crs?.properties?.href ?? undefined;

  if (parsed?.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
    raw = parsed.features;
  } else if (parsed?.type === 'Feature') {
    raw = [parsed];
  } else if (typeof parsed?.type === 'string' && parsed.coordinates) {
    // A bare geometry, e.g. {"type":"Point","coordinates":[…]}
    raw = [{ type: 'Feature', geometry: parsed, properties: {} }];
  } else {
    throw new LocalFileError('This JSON is not GeoJSON (no FeatureCollection, Feature, or geometry).');
  }

  // Features without drawable geometry would otherwise make Leaflet throw and
  // drop the whole collection — the same trap the ArcGIS path already guards.
  const usable = raw.filter(
    (feature) => feature?.geometry && Array.isArray(feature.geometry.coordinates),
  );
  const dropped = raw.length - usable.length;
  if (dropped > 0) notices.push(`${dropped} feature${dropped === 1 ? '' : 's'} had no geometry and were skipped.`);

  const features = usable.map((feature, index) => ({
    type: 'Feature',
    id: feature.id ?? index,
    geometry: feature.geometry,
    properties: feature.properties ?? {},
  })) as GeoJSON.Feature[];

  if (declaredCrs) {
    const resolved = resolveCrs(declaredCrs);
    if (resolved.error) {
      throw new LocalFileError(
        `This file declares the coordinate system "${resolved.name}", which could not be ` +
          `applied — ${resolved.error}`,
      );
    }
    if (resolved.convert) {
      for (const feature of features) reprojectGeometry(feature.geometry, resolved.convert);
      notices.push(`Reprojected from "${resolved.name}" to WGS84.`);
      if (needsDatumShift(declaredCrs)) {
        notices.push(
          'The file names a datum other than WGS84 and carries no transformation ' +
            'parameters, so positions may be out by up to a few hundred metres.',
        );
      }
    }
  }

  /* No CRS was declared and the numbers are not degrees. The data IS
     projected; there is simply nothing in the file saying into what. Refusing
     is the honest answer — a guess would draw the layer confidently in the
     wrong place. */
  if (looksProjected(features)) {
    throw new LocalFileError(
      declaredCrs
        ? 'Coordinates still fall outside longitude/latitude range after reprojection, so the declared coordinate system does not match the data.'
        : 'These coordinates are not longitude/latitude and the file does not say what projection they use. Re-export it as WGS84 (EPSG:4326), or supply it as a zipped shapefile with its .prj.',
    );
  }

  return summarise(name, features, notices);
}

/* ───────────────────────────── Delimited text ───────────────────────────── */

/** Splits one delimited line, honouring double-quoted fields. */
function splitRow(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (quoted) {
      if (char === '"') {
        if (line[index + 1] === '"') {
          value += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        value += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === delimiter) {
      out.push(value);
      value = '';
    } else {
      value += char;
    }
  }
  out.push(value);
  return out.map((entry) => entry.trim());
}

function parseDelimited(name: string, text: string): ParsedLocalLayer {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length < 2) throw new LocalFileError('This file has no data rows.');

  // Pick whichever delimiter yields more columns in the header.
  const delimiter = splitRow(lines[0], '\t').length > splitRow(lines[0], ',').length ? '\t' : ',';
  const header = splitRow(lines[0], delimiter);
  const lower = header.map((column) => column.toLowerCase());

  const findColumn = (aliases: string[]) => lower.findIndex((column) => aliases.includes(column));
  const lonIndex = findColumn(COORD_ALIASES.lon);
  const latIndex = findColumn(COORD_ALIASES.lat);

  if (lonIndex < 0 || latIndex < 0) {
    throw new LocalFileError(
      `No coordinate columns found. Expected longitude (${COORD_ALIASES.lon
        .slice(0, 4)
        .join(', ')}…) and latitude (${COORD_ALIASES.lat.slice(0, 4).join(', ')}…).`,
    );
  }

  const notices: string[] = [];
  const features: GeoJSON.Feature[] = [];
  let skipped = 0;

  for (let row = 1; row < lines.length; row += 1) {
    const cells = splitRow(lines[row], delimiter);
    const longitude = Number(cells[lonIndex]);
    const latitude = Number(cells[latIndex]);

    // Out-of-range values usually mean a projected CSV, which we cannot
    // reproject without knowing its CRS.
    if (
      !Number.isFinite(longitude) ||
      !Number.isFinite(latitude) ||
      Math.abs(longitude) > 180 ||
      Math.abs(latitude) > 90
    ) {
      skipped += 1;
      continue;
    }

    const properties: Record<string, unknown> = {};
    header.forEach((column, index) => {
      properties[column || `column_${index + 1}`] = cells[index] ?? '';
    });

    features.push({
      type: 'Feature',
      id: row,
      geometry: { type: 'Point', coordinates: [longitude, latitude] },
      properties,
    });
  }

  if (skipped > 0) {
    notices.push(
      `${skipped} row${skipped === 1 ? '' : 's'} skipped — missing or out-of-range coordinates (values must be WGS84 lon/lat).`,
    );
  }

  return summarise(name, features, notices);
}

/* ────────────────────────────── Entry point ─────────────────────────────── */

/** Expands a KML document into one ParsedLocalLayer per folder. */
function fromKml(text: string, fallbackName: string): ParsedImport {
  const { layers, notices } = parseKmlText(text, fallbackName);
  return {
    layers: layers.map((layer) =>
      summarise(layer.name, layer.features, layer.notices, {
        renderer: layer.renderer,
        styleCount: layer.styleCount,
        geometryType: layer.geometryType,
      }),
    ),
    notices,
  };
}

/**
 * A zipped shapefile — or several, when a whole folder of them was zipped up
 * together. EVERY .shp found in the archive becomes its own layer (matching
 * the "one file can produce several layers" KML-with-folders behaviour
 * above), not just whichever one happened to sort first — a folder of
 * shapefiles zipped as-is is a completely normal way to hand this app a
 * dataset, and silently keeping only one of them was the bug.
 */
async function fromZippedShapefile(file: File, base: string): Promise<ParsedImport> {
  if (file.size > MAX_LOCAL_FILE_BYTES) {
    throw new LocalFileError(
      `This archive is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit for a ` +
        `shapefile is ${MAX_LOCAL_FILE_BYTES / 1024 / 1024} MB — publish larger ` +
        'datasets through GeoServer or ArcGIS instead.',
    );
  }

  const entries = await readZip(await file.arrayBuffer());
  const shpNames = Array.from(entries.keys()).filter(
    (entry) => /\.shp$/i.test(entry) && !entry.startsWith('__MACOSX'),
  );
  if (shpNames.length === 0) {
    throw new LocalFileError('This ZIP contains no .shp file.');
  }

  const layers: ParsedLocalLayer[] = [];
  const notices: string[] = [];

  for (const shpName of shpNames) {
    const stem = shpName.replace(/\.shp$/i, '');
    const sibling = (extension: string) =>
      entries.get(`${stem}.${extension}`) ?? entries.get(`${stem}.${extension.toUpperCase()}`);
    const layerBase = stem.split('/').pop() || base;

    try {
      const prj = sibling('prj');
      const { features, notices: layerNotices, name } = buildShapefileFeatures(
        layerBase,
        entries.get(shpName) as Uint8Array,
        sibling('dbf'),
        prj ? new TextDecoder().decode(prj) : undefined,
      );
      const layer = summarise(name, features, layerNotices);
      // Several shapefiles in one archive share one notices list on the
      // layer picker, so each note needs to say which shapefile it is about.
      layer.notices = layerNotices.map((note) => `${layerBase}: ${note}`);
      layers.push(layer);
    } catch (error) {
      // One unreadable shapefile in the archive must not cost the rest of
      // them — the same per-item tolerance every other multi-item import in
      // this app already has (ArcGIS service loading, KML folders).
      const reason = error instanceof Error ? error.message : String(error);
      notices.push(`${layerBase}: ${reason}`);
    }
  }

  if (layers.length === 0) {
    throw new LocalFileError(notices[0] ?? 'No usable features were found in this archive.');
  }

  return { layers, notices };
}

/**
 * A GeoTIFF, parsed in this tab — see geotiff.ts for why there is no vector
 * `data` to speak of here, only `raster`.
 */
async function fromGeoTiff(file: File, base: string): Promise<ParsedImport> {
  let parsed;
  try {
    parsed = await parseGeoTiffFile(file);
  } catch (error) {
    throw new LocalFileError(
      error instanceof Error ? error.message : 'This GeoTIFF could not be read.',
    );
  }

  const layer: ParsedLocalLayer = {
    name: base,
    data: { type: 'FeatureCollection', features: [] },
    featureCount: 0,
    geometryType: 'unknown',
    fields: [],
    bounds: parsed.bounds,
    notices: parsed.notices,
    raster: {
      base64: parsed.base64,
      mimeType: parsed.mimeType,
      width: parsed.width,
      height: parsed.height,
      bandCount: parsed.bandCount,
      projectionEpsg: parsed.projectionEpsg,
      fileName: file.name,
    },
  };
  return { layers: [layer], notices: [] };
}

/**
 * A plain PNG/JPG — a photo, a scan, a screenshot — with no georeferencing of
 * its own, unlike a GeoTIFF. `center`/`zoom` are the current map view, which
 * is where it lands by default (see geotiff.ts's `boundsForImageAtView`);
 * callers that have no map open (today, only the dashboard's data-source
 * picker) fall back to a fixed, arbitrary view rather than failing outright.
 */
async function fromPlainImage(
  file: File,
  base: string,
  center: [number, number],
  zoom: number,
): Promise<ParsedImport> {
  let parsed;
  try {
    parsed = await parsePlainImageFile(file, center, zoom);
  } catch (error) {
    throw new LocalFileError(
      error instanceof Error ? error.message : 'This image could not be read.',
    );
  }

  const layer: ParsedLocalLayer = {
    name: base,
    data: { type: 'FeatureCollection', features: [] },
    featureCount: 0,
    geometryType: 'unknown',
    fields: [],
    bounds: parsed.bounds,
    notices: parsed.notices,
    raster: {
      base64: parsed.base64,
      mimeType: parsed.mimeType,
      width: parsed.width,
      height: parsed.height,
      bandCount: parsed.bandCount,
      fileName: file.name,
    },
  };
  return { layers: [layer], notices: [] };
}

/**
 * A zipped File Geodatabase, read by GDAL on the server.
 *
 * The upload returns a DESCRIPTION of every feature class, not its features:
 * name, geometry type, count, fields. Each layer therefore comes back
 * `pending` — the picker can show the whole geodatabase immediately, and only
 * the feature classes the user actually ticks are ever converted.
 */
async function fromGeodatabase(file: File): Promise<ParsedImport> {
  if (file.size > MAX_GDB_FILE_BYTES) {
    throw new LocalFileError(
      `This geodatabase is ${(file.size / 1024 / 1024).toFixed(0)} MB. The limit is ` +
        `${MAX_GDB_FILE_BYTES / 1024 / 1024} MB — publish larger datasets through ` +
        'GeoServer or ArcGIS instead.',
    );
  }

  let upload;
  try {
    upload = await uploadGeodatabase(file);
  } catch (error) {
    throw new LocalFileError(
      error instanceof Error ? error.message : 'This geodatabase could not be read.',
    );
  }

  const layers: ParsedLocalLayer[] = upload.layers.map((summary) => {
    /* Stated up front rather than discovered on "Add to map". A picker that
       lets someone tick five layers and then fails on two of them has wasted
       their time; one that greys those two out has answered the question. */
    const unavailable = !summary.hasGeometry
      ? /* Checked before the count, because a table with a thousand rows and no
           geometry column would otherwise look importable and arrive empty. */
        'Attribute table — no geometry to map.'
      : summary.featureCount === 0
        ? 'Empty — no features to add.'
        : summary.featureCount > upload.maxFeaturesPerLayer
          ? `Too large for a browser layer (${summary.featureCount.toLocaleString()} features, ` +
            `limit ${upload.maxFeaturesPerLayer.toLocaleString()}).`
          : undefined;

    return {
      name: summary.name,
      // Empty until `pending` runs. Nothing reads it before then: the picker
      // shows the counts and fields the server already described.
      data: { type: 'FeatureCollection', features: [] },
      featureCount: summary.featureCount,
      geometryType: summary.geometryType as LayerGeometryType,
      fields: summary.fields,
      bounds: summary.bounds ?? undefined,
      notices: summary.notices,
      unavailable,
      pending: async () => {
        const loaded = await fetchGeodatabaseLayer(upload.token, summary.index);
        const data = loaded.data ?? { type: 'FeatureCollection', features: [] };
        return {
          name: loaded.name,
          data,
          featureCount: loaded.featureCount,
          geometryType: loaded.geometryType as LayerGeometryType,
          fields: loaded.fields,
          bounds: computeBounds(data),
          notices: loaded.notices,
        };
      },
    };
  });

  const notices = [
    `Read on the server from ${upload.dataset}. The uploaded copy is deleted after ` +
      `${Math.round(upload.expiresInSeconds / 60)} minutes.`,
  ];

  return { layers, notices, release: () => void releaseGeodatabase(upload.token) };
}

/**
 * Turns whatever the picker selected into layers that actually carry features.
 *
 * A no-op for every format parsed in the browser — their features were there
 * all along. For a geodatabase this is where the chosen feature classes are
 * converted, which is why it is async and why the caller shows progress.
 */
export async function resolveLocalLayers(
  layers: ParsedLocalLayer[],
  onProgress?: (done: number, total: number) => void,
): Promise<ParsedLocalLayer[]> {
  const resolved: ParsedLocalLayer[] = [];

  // Sequentially, not in parallel: each conversion is GDAL work on one shared
  // container, and firing ten at once would make all ten slower while giving
  // the user no output until the last finished.
  for (const layer of layers) {
    onProgress?.(resolved.length, layers.length);
    if (!layer.pending) {
      resolved.push(layer);
      continue;
    }
    try {
      resolved.push(await layer.pending());
    } catch (error) {
      throw new LocalFileError(
        error instanceof Error
          ? `${layer.name}: ${error.message}`
          : `"${layer.name}" could not be loaded.`,
      );
    }
  }

  onProgress?.(resolved.length, layers.length);
  return resolved;
}

/**
 * `center`/`zoom` are the map's current view, needed only by a plain
 * PNG/JPG (no georeferencing of its own — see `fromPlainImage`); every other
 * format ignores them. Optional because the dashboard's data-source picker
 * calls this with no map open at all.
 */
export async function parseLocalFile(
  file: File,
  center?: [number, number],
  zoom?: number,
): Promise<ParsedImport> {
  try {
    // Stamped once here rather than in each of the eight format branches,
    // which all know their layer names but not the file they came out of.
    return { ...(await readFile(file, center, zoom)), sourceName: file.name };
  } catch (error) {
    // Surface parser messages verbatim; they name the actual problem.
    if (error instanceof LocalFileError) throw error;
    throw new LocalFileError(
      error instanceof Error ? error.message : 'This file could not be read.',
    );
  }
}

async function readFile(
  file: File,
  center: [number, number] = [0, 0],
  zoom: number = 10,
): Promise<ParsedImport> {
  const lower = file.name.toLowerCase();
  const base = file.name.replace(/\.[^.]+$/, '');

  /* A ZIP is checked BEFORE the size limit, because which limit applies
     depends on what is inside it: a zipped shapefile is parsed in this tab and
     has to fit in it, while a zipped geodatabase is streamed to the server and
     is never held here at all. Reading the entry names costs two small ranged
     reads regardless of how large the archive is. */
  if (/\.zip$/.test(lower)) {
    const entries = await peekZipEntryNames(file);
    if (looksLikeGeodatabase(entries)) return fromGeodatabase(file);
    return fromZippedShapefile(file, base);
  }

  // Checked before the general size limit: a raster gets its own, tighter one
  // (see geotiff.ts), and it is not a vector format the fallback message below
  // would describe correctly either.
  if (/\.(tif|tiff)$/i.test(lower)) return fromGeoTiff(file, base);
  if (/\.(png|jpe?g)$/i.test(lower)) return fromPlainImage(file, base, center, zoom);

  if (file.size > MAX_LOCAL_FILE_BYTES) {
    throw new LocalFileError(
      `This file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${
        MAX_LOCAL_FILE_BYTES / 1024 / 1024
      } MB — publish larger datasets through GeoServer or ArcGIS instead.`,
    );
  }

  /* A .gdb is a FOLDER, and a browser file input cannot pick one. Reaching
     here means the user selected a single file from inside it, which on its
     own is meaningless — so this says what to do rather than what went
     wrong. */
  if (/\.(gdb|gdbtable|gdbtablx|gdbindexes|freelist)$/.test(lower)) {
    throw new LocalFileError(
      'A File Geodatabase is a folder, not a file, so it has to be zipped first. ' +
        'Right-click the .gdb folder, compress it, and add the .zip.',
    );
  }

  if (/\.gpkg$/.test(lower)) {
    throw new LocalFileError(
      'Put the .gpkg inside a .zip and add that — GeoPackages are read on the ' +
        'server, and the upload takes an archive.',
    );
  }

  // A bare .shp has no attributes or projection with it.
  if (/\.(shp|dbf|shx|prj)$/.test(lower)) {
    if (!/\.shp$/.test(lower)) {
      throw new LocalFileError(
        'Add the .shp — or better, zip the .shp, .dbf and .prj together so attributes and the projection come with it.',
      );
    }
    const { features, notices, name } = buildShapefileFeatures(
      base,
      new Uint8Array(await file.arrayBuffer()),
      undefined,
      undefined,
    );
    return { layers: [summarise(name, features, notices)], notices: [] };
  }

  // KMZ is a ZIP, so it must be read as bytes rather than text.
  if (/\.kmz$/.test(lower)) {
    return fromKml(await extractKmlFromKmz(await file.arrayBuffer()), base);
  }

  const text = await file.text();

  if (/\.kml$/.test(lower)) return fromKml(text, base);

  if (/\.(geojson|json)$/.test(lower)) return { layers: [parseGeoJson(base, text)], notices: [] };
  if (/\.(csv|tsv|txt)$/.test(lower))
    return { layers: [parseDelimited(base, text)], notices: [] };

  // Unknown extension: infer from the content rather than refusing outright.
  const trimmed = text.trimStart();
  if (trimmed.startsWith('{') || trimmed.startsWith('['))
    return { layers: [parseGeoJson(base, text)], notices: [] };
  if (/^<\?xml|<kml/i.test(trimmed)) return fromKml(text, base);
  return { layers: [parseDelimited(base, text)], notices: [] };
}
